import { IpfError } from '@ipfabric/frontend-shared-assets/utils/IpfError';
import { omitUndefined } from '@ipfabric/utils';
import { record } from 'fp-ts';
import { flow } from 'fp-ts/function';
import type { AnyNonNullish } from 'utils/types/AnyNonNullish';

/**
 * Create URL with escaped query string attributes
 *
 * Automatic escaping of url path components is not implemented yet.
 * If we need that in future, we can extend implementation e.g. this way:
 * ```
 * createUrl(
 *   '/languages/{name}',
 *   {
 *     name: 'c++',
 *     compareWith: 'c#'
 *   },
 * ) -> '/languages/c%2B%2B?compareWith=c%23'
 * ```
 */
export const createUrl = (
  url: string,
  attributes: Record<string, string>,
): string =>
  `${url}${Object.keys(attributes).length > 0 ? '?' : ''}${Object.entries(
    attributes,
  )
    .map(
      ([key, value]) =>
        `${encodeURIComponent(key)}=${encodeURIComponent(value)}`,
    )
    .join('&')}`;

/**
 * Convert an object that is usually sent as POST request body to GET attributes
 *
 * Usage: In some cases like native browser downloads via `a href` we need to have a GET endpoint
 * that accepts the same body as a primary POST variant. This helper converts an arbitrary object
 * to GET parameters.
 *
 * Known pitfall: only object, array, string, number, bigint and undefined (= omitted) types
 * are supported at top level, as only objects and arrays are deserialized as JSON on backend.
 * This is to be revisited in the future.
 */
export const objectToUrlAttributes: (
  data: AnyNonNullish,
) => Record<string, string> = flow(
  omitUndefined,
  record.map((x) => {
    if (x !== null && typeof x === 'object') {
      return JSON.stringify(x);
    }
    if (
      typeof x === 'string' ||
      typeof x === 'number' ||
      typeof x === 'bigint'
    ) {
      return x.toString();
    }
    throw new IpfError(
      'INVALID_ARGUMENT',
      `invalid variable type ${x === null ? 'null' : typeof x}`,
    );
  }),
);
